/**
 * Pass to the Copy button onClick handler
 */
export function copyToClipboard() {
    document.execCommand('copy');
}

/**
 * Creates an event listener that should be added/removed from the document on component mount/unmount
 *
 * @param getLogs function that returns the logs to be copied
 * @param orderedLogKeys ordered list of string keys for the log properties to list in the copied table
 * @param parseLog optional argument that specifies how to display each log property as a string
 */
export function createCopyEventListener<T extends object>(
    getLogs: () => T[],
    orderedLogKeys: Extract<keyof T, string>[],
    parseLog?: (log: T) => { [K in keyof T]: string }
) {
    return (e: ClipboardEvent): void => {
        e.preventDefault();
        e.clipboardData.setData('text/html', getHtmlFragment(getLogs, orderedLogKeys, parseLog));
    };
}

/**
 * Helper function that takes an arbitrary value and transforms it into a JSON string representation
 */
export function getObjectAsString(value: any): string {
    return value ? JSON.stringify(value).replace(/["]+/g, "'") : '';
}

function getHtmlFragment<T extends object>(
    getLogs: () => T[],
    orderedLogKeys: Extract<keyof T, string>[],
    parseLog?: (log: T) => { [K in keyof T]: string }
): string {
    const headContent = createSingleRow(
        'background-color: #106EBE; color: white;',
        orderedLogKeys.map(key => createSingleCell('th', capitalizeFirstLetter(key)))
    );

    const bodyContent = getLogs()
        .map(log => {
            const parsedLog = parseLog ? parseLog(log) : log;
            return (
                createSingleRow(
                    'background-color: #FFFFFF',
                    orderedLogKeys.map(key => createSingleCell('td', parsedLog[key]))
                ) + '\n'
            );
        })
        .join('');

    const tableContent = `<thead style='background-color: #106EBE; color: white;'>${headContent}</thead><tbody>${bodyContent}</tbody>`;
    return `<table cellpadding='0' cellspacing='0' style='font-family: Calibri, sans-serif; font-size: 11pt; border-collapse: collapse;'>${tableContent}</table>`;
}

function createSingleCell(cellType: 'th' | 'td', cellValue: any): string {
    return `<${cellType} style='border: 1px solid white; vertical-align: top; padding: 1.45pt .05in;'>${cellValue}</${cellType}>`;
}

function createSingleRow(rowStyle: string, cells: string[]): string {
    return `<tr style=${rowStyle}>${cells.join('\n')}\n</tr>`;
}

function capitalizeFirstLetter(value: string | number): string {
    const str = value.toString();
    return str.charAt(0).toUpperCase() + str.slice(1);
}
